import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import { Input as BaseInput, Button, FormError } from '../components/FormStyle';
import { PageTitle } from '../components/Style';
import { VinylDisc, VINYL_THEMES } from '../components/UserBadge';
import ShareModal from '../components/ShareModal';
import { trackEvent } from '../analytics';

var Container = styled.div`
  box-sizing: border-box;
  max-width: 410px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  flex-direction: column;
`;

var TextArea = styled.textarea`
  padding: 1rem;
  border: 1px solid #999;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.8rem;
  background-color: #fff;
  color: #000;
  width: 100%;
  box-sizing: border-box;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
`;

var SuccessMessage = styled.div`
  background: #e8f5e9;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  text-align: center;
  color: #2e7d32;
  font-size: 0.8rem;
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

var Input = styled(BaseInput)`
  width: 100%;
  box-sizing: border-box;
`;

function Invites() {
  useEffect(function () {
    document.title = 'Invite Friends | HeavyRotation';
  }, []);

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var shareUrl = authTokens?.user?.username
    ? 'https://heavyrotation.app/profile/' + authTokens.user.username
    : null;
  var shareUrlWithRef =
    shareUrl && authTokens?.user?.id ? shareUrl + '?ref=' + authTokens.user.id : shareUrl;

  var _showShare = useState(false);
  var showShare = _showShare[0];
  var setShowShare = _showShare[1];

  var emailState = useState('');
  var email = emailState[0];
  var setEmail = emailState[1];

  var messageState = useState('');
  var message = messageState[0];
  var setMessage = messageState[1];

  var submittingState = useState(false);
  var isSubmitting = submittingState[0];
  var setIsSubmitting = submittingState[1];

  var errorState = useState('');
  var error = errorState[0];
  var setError = errorState[1];

  var successState = useState(false);
  var success = successState[0];
  var setSuccess = successState[1];

  function handleSubmit() {
    var emails = email
      .split(',')
      .map(function (e) {
        return e.trim();
      })
      .filter(function (e) {
        return e.length > 0;
      });

    if (emails.length === 0) {
      setError('Please enter at least one email address.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess(false);

    api
      .post('/api/invites/simple-invite', {
        emails: emails,
        message: message.trim(),
      })
      .then(function () {
        setSuccess(true);
        setEmail('');
        setMessage('');
        trackEvent('invite_sent', { count: emails.length });
        setIsSubmitting(false);
      })
      .catch(function (e) {
        var reason = e.response?.data?.reason || 'Failed to send invites.';
        setError(reason);
        setIsSubmitting(false);
      });
  }

  return (
    <Container>
      <div
        style={{
          width: '100%',
          padding: '1.25rem',
          background: 'white',
          border: '1px solid #ddd',
          borderRightWidth: 2,
          borderBottomWidth: 2,
          borderRadius: 12,
          boxSizing: 'border-box',
          marginTop: '1.5rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <VinylDisc theme={VINYL_THEMES.test_pressing} size={56} spinning />
        <div>
          <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '0.25em' }}>
            Music is better with friends
          </div>
          <div style={{ color: '#666', fontSize: '0.85rem', lineHeight: 1.4 }}>
            Invite your friends to join HeavyRotation.
          </div>
        </div>
      </div>

      <button
        onClick={function () {
          setShowShare(true);
        }}
        style={{
          width: '100%',
          background: 'white',
          color: 'black',
          border: '1px solid black',
          borderRightWidth: '2px',
          borderBottomWidth: '2px',
          borderRadius: 8,
          padding: '0.6rem 1rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Share My Profile
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          margin: '1rem 0',
          gap: '0.75rem',
        }}
      >
        <div style={{ flex: 1, height: 1, background: '#ddd' }} />
        <span style={{ color: '#999', fontSize: '0.75rem', fontWeight: 600 }}>or</span>
        <div style={{ flex: 1, height: 1, background: '#ddd' }} />
      </div>

      <button
        onClick={function () {
          var smsText =
            'Join me on HeavyRotation and discover new music! ' +
            (shareUrlWithRef || 'https://heavyrotation.app');
          window.location.href = 'sms:?&body=' + encodeURIComponent(smsText);
        }}
        style={{
          width: '100%',
          background: 'white',
          color: 'black',
          border: '1px solid black',
          borderRightWidth: '2px',
          borderBottomWidth: '2px',
          borderRadius: 8,
          padding: '0.6rem 1rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Text a Friend
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          margin: '1rem 0',
          gap: '0.75rem',
        }}
      >
        <div style={{ flex: 1, height: 1, background: '#ddd' }} />
        <span style={{ color: '#999', fontSize: '0.75rem', fontWeight: 600 }}>or</span>
        <div style={{ flex: 1, height: 1, background: '#ddd' }} />
      </div>

      {showShare && (
        <ShareModal
          url={shareUrl}
          title="Share Profile"
          shareType="profile"
          imageData={{
            profilePicURL: authTokens?.user?.profilePictureURL,
            displayName: authTokens?.user?.displayName || authTokens?.user?.username,
            username: authTokens?.user?.username,
            recentArtwork: [],
          }}
          onClose={function () {
            setShowShare(false);
          }}
        />
      )}

      {success && <SuccessMessage>Invites sent!</SuccessMessage>}
      {error && <FormError>{error}</FormError>}

      <label
        style={{ width: '100%', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.35rem' }}
      >
        Email addresses
      </label>
      <Input
        type="text"
        placeholder="friend@email.com, another@email.com"
        value={email}
        onChange={function (e) {
          setEmail(e.target.value);
        }}
      />

      <TextArea
        placeholder="Add a personal message"
        value={message}
        onChange={function (e) {
          setMessage(e.target.value);
        }}
        maxLength={500}
      />

      <div style={{ width: '100%' }}>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Sending...' : 'Send Invite'}
        </Button>
      </div>
    </Container>
  );
}

export default Invites;
